import React, { forwardRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, PlusIcon, XIcon } from '@heroicons/react/solid';
import Asterix from '../../assets/asterix.svg';
import { Input, Label, TextArea } from '../AccountProfile/ProfileStyles';
import styled from 'styled-components/macro';
import { Switch, ToggleButton } from '../shared/Fields/Switch';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import {
  METADATA_INSTANCE_DATA_TYPE_USER_INPUTS,
  METADATA_OBJECT_DATA_TYPE_ENUM,
  METADATA_OBJECT_DATA_TYPE_BOOLEAN,
  METADATA_OBJECT_DATA_TYPE_DATE,
  METADATA_OBJECT_DATA_TYPE_DATETIME,
  METADATA_OBJECT_DATA_TYPE_INTEGER,
  METADATA_OBJECT_DATA_TYPE_NUMBER,
  METADATA_OBJECT_DATA_TYPE_OBJECT,
  METADATA_OBJECT_DATA_TYPE_OBJECT_ARRAY,
  METADATA_OBJECT_DATA_TYPE_STRING_ARRAY,
  METADATA_OBJECT_DATA_TYPE_TIME,
  METADATA_OBJECT_DATA_TYPE_STRING,
  METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_TYPES,
  METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_MAP,
  isNumericType
} from './metadataUtil';
import InputField from '../shared/Fields/InputField';
import { AppConstants } from '../../constants/AppConstants';
import PickList, { PICKLIST_TYPES } from '../shared/Fields/PickList';
import { isArray, isUndefined } from 'lodash';
import InfoPopover from '../shared/Fields/InfoPopover';

const DEFAULT_KEY_ID = 'key';
const DEFAULT_VALUE_KEY = 'value';

const Image = styled.img`
  width: 8px;
  height: 8px;
`;

const ErrorLabel = styled.span`
  fontsize: 11px;
  color: red;
`;

interface FormSchema {
  name: string;
  type: string;
  editable?: boolean;
  maxLength?: number;
  isRequired?: boolean;
  min?: number;
  max?: number;
  attributes?: FormSchema[];
  value?: any;
  keyPath?: string;
  enum?: any;
  minItems?: number;
  maxItems?: number;
}

interface JSONFormCreatorProps {
  schema: FormSchema[];
  setSchema: Function;
  disabled: boolean;
  addToSchemaArray: Function;
  deleteFromSchemaArray: Function;
  addDeleteToStringArray: Function;
  isEditMode: boolean;
}

const calculateRows = (content: string) => {
  let length = content.length;
  return length > 64 && length < 128
    ? 2
    : length >= 128 && length < 255
    ? 3
    : length >= 255
    ? 4
    : 1;
};

const JSONFormCreator: React.FC<JSONFormCreatorProps> = ({
  schema,
  setSchema,
  disabled,
  addToSchemaArray,
  deleteFromSchemaArray,
  addDeleteToStringArray,
  isEditMode
}) => {
  const { t } = useTranslation();

  const [collapsedMap, setCollapsedMap] = useState<{ [key: string]: boolean }>({}); //eg {test1:false}

  const toggleCollapse = (fieldName: string) => {
    setCollapsedMap((prevState) => ({
      ...prevState,
      [fieldName]: !prevState[fieldName]
    }));
  };

  const renderPrimitiveField = (
    field: any,
    setSchemaFunction: any,
    isNestedNumberType: boolean = false
  ) => {
    const {
      name,
      type,
      editable = true,
      maxLength,
      isRequired = false,
      min,
      max,
      value,
      keyPath = '',
      enum: enumTypes = [],
      tempValue,
      error
    } = field;

    const DateInputField = forwardRef(({ value, onClick }: any, ref) => (
      <Input
        type={'text'}
        id={name}
        name={name}
        className="!w-64 rounded border px-3 py-2"
        required={isRequired}
        value={value}
        onClick={onClick}
        ref={ref}
        onChange={(e: any) => {}}
        data-testid="datetime-input"
        disabled={disabled || (isEditMode && !editable)} //|| !editable
      />
    ));

    const boolValue: boolean = /true/i.test(value);

    switch (type) {
      case METADATA_OBJECT_DATA_TYPE_BOOLEAN:
        return (
          <div className="flex flex-row">
            <Switch
              toggle={boolValue}
              onClick={() => setSchemaFunction({ value: !boolValue })}
              data-testid={'boolean-field'}
              disabled={!editable}
              id={name}
            >
              <ToggleButton toggle={boolValue} />
            </Switch>
            <div className="ml-3 self-end">{boolValue ? `${t('TRUE')}` : `${t('FALSE')}`}</div>
          </div>
        );
      case METADATA_OBJECT_DATA_TYPE_ENUM:
        return enumTypes?.length && typeof enumTypes[0] === 'string' ? (
          <div className="!w-full">
            <PickList
              fieldKey={DEFAULT_KEY_ID}
              fieldValues={[DEFAULT_VALUE_KEY]}
              selectedItems={[{ [DEFAULT_KEY_ID]: value, [DEFAULT_VALUE_KEY]: value }]}
              onSelectedChange={(item: any) => {
                setSchemaFunction({ value: item.length ? item[0][DEFAULT_VALUE_KEY] : [] });
              }}
              items={enumTypes?.map((item: string) => {
                return { key: item, value: item };
              })}
              disabled={disabled || (isEditMode && !editable)}
              readOnly={isEditMode && !editable}
              type={PICKLIST_TYPES.SINGLE}
            />
          </div>
        ) : (
          ''
        );

      case METADATA_OBJECT_DATA_TYPE_DATE:
        return (
          <DatePicker
            selected={value ? new Date(value) : null}
            disabledKeyboardNavigation
            dateFormat={`yyyy-MM-dd`}
            isClearable={value !== '' && !isUndefined(value)}
            scrollableYearDropdown
            showMonthDropdown
            className="w-auto"
            showYearDropdown
            yearDropdownItemNumber={100}
            wrapperClassName="w-auto"
            onChange={(date: any) => {
              setSchemaFunction({
                value: date
                  ? moment(date).format(AppConstants.DEFAULT_METADATA_INSTANCE_DATE_FORMAT)
                  : ''
              });
            }}
            customInput={<DateInputField />}
          />
        );

      case METADATA_OBJECT_DATA_TYPE_DATETIME:
        return (
          <DatePicker
            selected={value ? new Date(value) : null}
            isClearable={value !== '' && !isUndefined(value)}
            wrapperClassName="w-auto"
            disabledKeyboardNavigation
            showTimeSelect
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={100}
            timeIntervals={30}
            dateFormat={'yyyy-MM-dd h:mm aa'}
            onChange={(date: any) => {
              setSchemaFunction({
                value: date
                  ? moment(date)
                      .utc()
                      .format(AppConstants.DEFAULT_METADATA_INSTANCE_UTC_DATETIME_FORMAT)
                  : ''
              });
            }}
            customInput={<DateInputField />}
          />
        );

      case METADATA_OBJECT_DATA_TYPE_TIME:
        return (
          <DatePicker
            selected={
              value
                ? new Date(
                    `01/01/1970 ${moment(
                      value,
                      AppConstants.DEFAULT_METADATA_INSTANCE_TIME_FORMAT
                    ).format('HH:mm:ss')}`
                  )
                : null
            }
            isClearable={value !== '' && !isUndefined(value)}
            wrapperClassName="w-auto"
            disabledKeyboardNavigation
            showTimeSelect
            showTimeSelectOnly
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={100}
            timeIntervals={30}
            dateFormat={`h:mm aa`}
            onChange={(date: any) => {
              setSchemaFunction({
                value: date
                  ? moment(date).format(AppConstants.DEFAULT_METADATA_INSTANCE_TIME_FORMAT)
                  : ''
              });
            }}
            customInput={<DateInputField />}
          />
        );

      case METADATA_OBJECT_DATA_TYPE_STRING:
        return (
          <TextArea
            id={name}
            data-testid={`textfield`}
            name={name}
            className={`!w-3/4 rounded border px-3 py-2 ${value.length > 255 && '!resize-y'}`}
            maxLength={maxLength}
            required={isRequired}
            value={value}
            onChange={(e: any) => {
              setSchemaFunction({ value: e.target.value });
            }}
            disabled={disabled || (isEditMode && !editable)}
            rows={calculateRows(value)}
            cols={20}
          />
        );
      default:
        return (
          <div>
            <Input
              type={
                [METADATA_OBJECT_DATA_TYPE_NUMBER, METADATA_OBJECT_DATA_TYPE_INTEGER].includes(type)
                  ? 'number'
                  : type === METADATA_OBJECT_DATA_TYPE_DATETIME
                  ? 'datetime-local'
                  : type === METADATA_OBJECT_DATA_TYPE_DATE
                  ? 'date'
                  : 'text'
              }
              id={name}
              data-testid={`textfield`}
              name={name}
              className={`${
                [METADATA_OBJECT_DATA_TYPE_NUMBER, METADATA_OBJECT_DATA_TYPE_INTEGER].includes(type)
                  ? 'w-48'
                  : '!w-1/2'
              } rounded border px-3 py-2`}
              maxLength={maxLength}
              required={isRequired}
              value={tempValue === null || isNestedNumberType ? value : tempValue}
              onBlur={(e: any) => {
                if (tempValue === null || isNestedNumberType) {
                  return;
                }
                let obj: any = {};
                if (tempValue === '') {
                  obj.tempValue = null;
                  setSchemaFunction(obj);
                  return;
                }
                let val =
                  type === METADATA_OBJECT_DATA_TYPE_NUMBER
                    ? parseFloat(tempValue)
                    : Number(tempValue);
                if (min !== undefined && Number(val) < min) {
                  obj.tempValue = null;
                  obj.error = t('T_MIN_VAL_ERR').replace('%s', min);
                  setSchemaFunction(obj);
                  return;
                }
                if (max !== undefined && Number(val) > max) {
                  obj.tempValue = null;
                  obj.error = t('T_MAX_VAL_ERR').replace('%s', max);
                  setSchemaFunction(obj);
                  return;
                }

                obj.value = val;
                obj.tempValue = null;
                setSchemaFunction(obj);
              }}
              onChange={(e: any) => {
                let obj: any = {};
                if (error?.length) {
                  obj.error = '';
                }
                if (isNumericType(type) && !isNestedNumberType) {
                  obj.tempValue = e.target.value;
                } else if (isNestedNumberType) {
                  let val =
                    type === METADATA_OBJECT_DATA_TYPE_NUMBER
                      ? parseFloat(e.target.value)
                      : Number(e.target.value);
                  obj.value = val;
                } else {
                  setSchemaFunction(e.target.value);
                  obj.value = e.target.value;
                }
                setSchemaFunction(obj);
              }}
              disabled={disabled || (isEditMode && !editable)}
            />
            <ErrorLabel>{error}</ErrorLabel>
          </div>
        );
    }
  };

  const renderField = (field: FormSchema) => {
    const {
      name,
      type,
      editable = true,
      maxLength,
      isRequired = false,
      min,
      max,
      attributes,
      value,
      keyPath = '',
      minItems,
      maxItems
    } = field;

    const indent = 5;
    const verticalMargin = 3;
    const dataType =
      type !== METADATA_OBJECT_DATA_TYPE_OBJECT &&
      type !== METADATA_OBJECT_DATA_TYPE_OBJECT_ARRAY &&
      type !== METADATA_OBJECT_DATA_TYPE_STRING_ARRAY &&
      !METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_TYPES.includes(type)
        ? `(${type})`
        : '';

    if (METADATA_INSTANCE_DATA_TYPE_USER_INPUTS.includes(type)) {
      return (
        <div key={name} className={`ml-${indent} mb-${verticalMargin}`}>
          <div className="flex flex-row">
            {(METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_TYPES.includes(type) ||
              type === METADATA_OBJECT_DATA_TYPE_STRING_ARRAY) &&
              !disabled && (
                <button className="mr-2 focus:outline-none" onClick={() => toggleCollapse(name)}>
                  {collapsedMap[name] ? (
                    <ChevronRightIcon className="h-6 w-6 text-black" />
                  ) : (
                    <ChevronDownIcon className="h-6 w-6 text-black" />
                  )}
                </button>
              )}
            <Label htmlFor={keyPath} className="flex items-center">
              {isRequired ? <Image src={Asterix} className="mr-1" /> : ''}
              {name} {dataType}
            </Label>
            {(METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_TYPES.includes(type) ||
              type === METADATA_OBJECT_DATA_TYPE_STRING_ARRAY) &&
            !disabled ? (
              !isUndefined(maxItems) && value.length === maxItems ? (
                <InfoPopover
                  className={`ml-2 mt-1`}
                  content={<div>{t('T_MAX_ITEMS_ERROR').replace('%s', maxItems.toString())}</div>}
                  customIcon={<PlusIcon className={`h-5 w-5 text-gray-500`} />}
                ></InfoPopover>
              ) : (
                <button
                  className="ml-2  hover:text-gray-500 focus:outline-none"
                  onClick={() => addDeleteToStringArray(keyPath, 'add')}
                  data-testid="String[]-addElement"
                >
                  <PlusIcon className="mb-1 h-5 w-5 text-black" />
                </button>
              )
            ) : (
              ''
            )}
          </div>

          {METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_TYPES.includes(type) ||
          type === METADATA_OBJECT_DATA_TYPE_STRING_ARRAY
            ? !collapsedMap[name] &&
              isArray(value) &&
              value?.map((arrValue: string, strIndex: number) => {
                return (
                  <div
                    className={`ml-${indent} ${
                      value.length - 1 !== strIndex && `mb-${verticalMargin}`
                    } relative right-2 border-l-[1px] border-dashed border-l-gray-600 pl-3`}
                  >
                    {value.length > 1 ? (
                      <div className="flex items-center">
                        <p className="mb-1 font-bold">[{strIndex}]</p>
                        {!disabled ? (
                          !isUndefined(minItems) && value.length === minItems ? (
                            <InfoPopover
                              className="ml-2"
                              content={
                                <div>
                                  {t('T_MIN_ITEMS_ERROR').replace('%s', minItems.toString())}
                                </div>
                              }
                              customIcon={<XIcon className={`h-6 w-6 text-gray-500`} />}
                            ></InfoPopover>
                          ) : (
                            <button
                              className="ml-2  hover:text-gray-500 focus:outline-none"
                              data-testid="String[]-removeElement"
                              onClick={() => addDeleteToStringArray(keyPath, 'delete', strIndex)}
                            >
                              <XIcon className="h-6 w-6 text-black" />
                            </button>
                          )
                        ) : (
                          ''
                        )}
                      </div>
                    ) : (
                      ''
                    )}

                    {METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_TYPES.includes(type) ? (
                      renderPrimitiveField(
                        {
                          type: METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_MAP[type],
                          name,
                          editable,
                          maxLength,
                          isRequired,
                          min,
                          max,
                          value: arrValue,
                          keyPath
                        },
                        (value: any) => {
                          setSchema(keyPath, value, strIndex);
                        },
                        isNumericType(METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_MAP[type])
                      )
                    ) : (
                      <TextArea
                        data-testid="String[]-textfield"
                        id={name}
                        name={name}
                        className={`!w-3/4  rounded border px-3 py-2 ${
                          arrValue.length > 255 && '!resize-y'
                        }`}
                        maxLength={maxLength}
                        required={isRequired}
                        value={arrValue}
                        onChange={(e: any) => {
                          setSchema(keyPath, { value: e.target.value }, strIndex);
                        }}
                        disabled={disabled || (isEditMode && !editable)}
                        rows={calculateRows(arrValue)}
                        cols={20}
                      />
                    )}
                  </div>
                );
              })
            : renderPrimitiveField(field, (value: any) => setSchema(keyPath, value, 0))}
        </div>
      );
    }
    if (
      type === METADATA_OBJECT_DATA_TYPE_OBJECT_ARRAY &&
      Array.isArray(attributes) &&
      Array.isArray(attributes[0])
    ) {
      return (
        <div key={name} className={`ml-${indent} mt-${verticalMargin}`}>
          <div className="flex items-center">
            <button className="mr-2 focus:outline-none" onClick={() => toggleCollapse(name)}>
              {collapsedMap[name] ? (
                <ChevronRightIcon className="h-6 w-6 text-black" />
              ) : (
                <ChevronDownIcon className="h-6 w-6 text-black" />
              )}
            </button>
            <Label htmlFor={keyPath} className="mb-1 flex items-center font-bold">
              {isRequired ? <Image src={Asterix} className="mr-1" /> : ''}
              {name}
            </Label>
            {!disabled ? (
              <button
                className="ml-2  hover:text-gray-500 focus:outline-none"
                onClick={() => addToSchemaArray(keyPath)}
              >
                <PlusIcon className="h-5 w-5 text-black" />
              </button>
            ) : (
              ''
            )}
          </div>

          {!collapsedMap[name] && (
            <div className={`ml-${indent}`}>
              {attributes?.map((subAttributes: any, subIndex: number) => (
                <div key={`${name}-${subIndex}`}>
                  <div className="flex items-center">
                    <button
                      className="mr-2 focus:outline-none"
                      onClick={() => toggleCollapse(`${name}-${subIndex}`)}
                    >
                      {collapsedMap[`${name}-${subIndex}`] ? ( //Add Collapse
                        <ChevronRightIcon className="h-6 w-6 text-black" />
                      ) : (
                        <ChevronDownIcon className="h-6 w-6 text-black" />
                      )}
                    </button>
                    <p className="mb-1 font-bold">[{subIndex}]</p>
                    {!disabled ? (
                      <button
                        className="ml-2  hover:text-gray-500 focus:outline-none"
                        onClick={() => deleteFromSchemaArray(keyPath, subIndex)}
                      >
                        <XIcon className="h-6 w-6 text-black" />
                      </button>
                    ) : (
                      ''
                    )}
                  </div>

                  {!collapsedMap[`${name}-${subIndex}`] && ( //Add Collapse TODO
                    <div className={`ml-${indent + 1}`}>
                      {subAttributes?.map((subAttribute: FormSchema, attributeIndex: number) => (
                        <JSONFormCreator
                          key={`${name}-${subIndex}-${attributeIndex}`}
                          schema={[subAttribute]}
                          setSchema={setSchema}
                          disabled={disabled}
                          addToSchemaArray={addToSchemaArray}
                          deleteFromSchemaArray={deleteFromSchemaArray}
                          addDeleteToStringArray={addDeleteToStringArray}
                          isEditMode={isEditMode}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (type === METADATA_OBJECT_DATA_TYPE_OBJECT_ARRAY && attributes) {
      return (
        <div key={name} className={`ml-${indent} mt-${verticalMargin}`}>
          <div className="flex items-center">
            <button className="mr-2 focus:outline-none" onClick={() => toggleCollapse(name)}>
              {collapsedMap[name] ? (
                <ChevronRightIcon className="h-6 w-6 text-black" />
              ) : (
                <ChevronDownIcon className="h-6 w-6 text-black" />
              )}
            </button>
            <Label htmlFor={keyPath} className="mb-1 flex items-center font-bold">
              {isRequired ? <Image src={Asterix} className="mr-1" /> : ''}
              {name}
            </Label>
            {!disabled ? (
              <button className="ml-2 focus:outline-none" onClick={() => addToSchemaArray(keyPath)}>
                <PlusIcon className="h-5 w-5 text-black" />
              </button>
            ) : (
              ''
            )}
          </div>

          {!collapsedMap[name] && (
            <div className={`ml-${indent + 1}`}>
              {attributes?.map((attribute) => (
                <JSONFormCreator
                  key={attribute.name}
                  schema={[attribute]}
                  setSchema={setSchema}
                  disabled={disabled}
                  addToSchemaArray={addToSchemaArray}
                  deleteFromSchemaArray={deleteFromSchemaArray}
                  addDeleteToStringArray={addDeleteToStringArray}
                  isEditMode={isEditMode}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    if (type === METADATA_OBJECT_DATA_TYPE_OBJECT && attributes) {
      return (
        <div key={name} className={`ml-${indent} mt-${verticalMargin}`}>
          <div className="flex items-center">
            <button className="mr-2 focus:outline-none" onClick={() => toggleCollapse(name)}>
              {collapsedMap[name] ? (
                <ChevronRightIcon className="h-6 w-6 text-black" />
              ) : (
                <ChevronDownIcon className="h-6 w-6 text-black" />
              )}
            </button>
            <Label htmlFor={keyPath} className="mb-1 flex items-center font-bold">
              {isRequired ? <Image src={Asterix} className="mr-1" /> : ''}
              {name}
            </Label>
          </div>

          {!collapsedMap[name] && (
            <div className={`ml-${indent + 1}`}>
              {Object.keys(attributes)?.map((attrName: any) => (
                <JSONFormCreator
                  key={attrName}
                  schema={[attributes[attrName]]}
                  setSchema={setSchema}
                  disabled={disabled}
                  addToSchemaArray={addToSchemaArray}
                  deleteFromSchemaArray={deleteFromSchemaArray}
                  addDeleteToStringArray={addDeleteToStringArray}
                  isEditMode={isEditMode}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <ul className="relative right-3 border-l-[1px] border-dashed border-l-gray-600">
      {schema?.map(renderField)}
    </ul>
  );
};

export default JSONFormCreator;
